import {
  BackToStartSolidIcon,
  Box,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Pagination,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  SPACE_BLOCK,
  desktopOnly,
  mobileOnly
} from "../../../components/UserInfo";

/**
 * 28px wide, down from Interstate's 40px + 4px margin (`PaginationStyle.js`) — the fix for a pager
 * that wrapped into **three** ragged rows: First alone, then Previous + the numbers, then Next.
 *
 * **The control count is not ours to choose.** Interstate's `Pagination` hardcodes
 * `boundaryCount: 0, siblingCount: 2` and exposes no prop for either, and its stylesheet sets
 * `.MuiPaginationItem-ellipsis{display:none}` so the two ellipses take no width. Working MUI's
 * `usePagination` through for every `count`, the peak is **nine** visible controls — Previous, seven
 * numbers, Next — and it lands at exactly `count === 7`, where the 5-page sibling window plus both
 * boundary pages covers the whole range and nothing is elided. That is not a hypothetical: a
 * 312-user account at 50 per page **is** 7 pages, which is the number this page ships with.
 *
 * Nine controls at the stock 44px apiece is 396px inside a **fixed 320px** rail, hence the wrap.
 * At 28px the worst case is 32 + 4 + 9 x 28 = **288px**, leaving slack for the First button's exact
 * `sm` width, which comes from a theme token rather than from anything checked in here.
 *
 * Width may shrink but the **height stays 32px**: 28 x 32 clears WCAG 2.2 AA 2.5.8's 24 x 24
 * minimum with the margins gone, and two digits at Interstate's 14px pager font need ~18px.
 *
 * **These apply to the desktop pager only.** The mobile pager is a different control — see
 * {@link ListPager}'s own comment for why the numbers cannot simply be grown.
 */
const PAGER_CONTROL_WIDTH = "28px";
const PAGER_CONTROL_HEIGHT = "32px";

export interface ListPagerProps {
  /** **Zero-based** — the wire's convention, as the query hook holds it. */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * The list's fixed pager (FR-014, **D-05**) — in two shapes, one per breakpoint.
 *
 * | | at/above `md` | below `md` |
 * |---|---|---|
 * | Controls | First, Previous, **numbered pages**, Next | First, Previous, **"Page n of m"**, Next, Last |
 * | Size | 28 x 32 | 48 x 48 (`size="lg"`) |
 * | Source | Interstate `Pagination` | plain `Button`s |
 *
 * ## Why the mobile pager drops the numbers rather than growing them (TA4236901)
 *
 * **Arithmetic, not preference.** At a 390px viewport the shell's 16px padding leaves **358px**, and
 * the mobile list panel is full-width — only 38px more than the 320px rail. Interstate's stock item
 * is `40px` wide with `margin: 0 2px`, a **44px pitch**, and the peak control count is nine:
 *
 * | Visible controls | Stock width | Fits 358px? |
 * |---|---|---|
 * | 7 (10 pages, mid-range) | 308px | yes |
 * | 8 (10 pages, page 1) | 352px | barely, and not with First |
 * | **9 (the peak, at `count === 7`)** | **396px** | **no** |
 *
 * So a thumb-sized numbered strip does not fit *the viewport*, never mind the panel — and
 * `siblingCount` cannot be lowered to shrink the count, because `Pagination` hardcodes it and drops
 * unknown props on the floor (it forwards only `addAnalyticsTag`'s single key, so a cast does not
 * help either). The existing 28 x 32 strip *does* fit at 390px and is AA-conformant; what it is not
 * is thumb-operable, which is what TA4236901 asked for.
 *
 * **"Load more" was the mockup's answer and was rejected.** It needs `useInfiniteQuery` — which
 * appears nowhere in this repo — to replace a `useQuery` keyed by `page`, and it hides how much of a
 * 453-user list is left. TA4236901's own premise is that search is the primary interaction, which
 * makes deep browsing rare: the pager needs to be *reachable*, not *infinite*.
 *
 * ## Both shapes are rendered, and CSS picks one
 *
 * `desktopOnly` / `mobileOnly` rather than a `useWindowDimensions` branch, because that hook
 * initialises to all-`false` and corrects in an effect — on a desktop load it would paint the mobile
 * pager for one frame. Pure CSS is right on the first frame. The two carry **different
 * `data-testid`s** so a test can assert that exactly one is visible at a given width; asserting on a
 * shared id would pass while both were in the DOM.
 *
 * ## Localization: the mobile shape fixes a pre-existing gap
 *
 * Previous/Next inside `Pagination` carry MUI's hardcoded English ("Go to previous page"), with no
 * prop to override them — the same defect that keeps First out of `showFirstButton` below. Rendering
 * them as our own buttons lets them take `PREVIOUS_PAGE` / `NEXT_PAGE`, so the mobile pager is fully
 * localized where the desktop one still is not. Every label here is already registered in Label
 * Services; none was invented for this.
 *
 * **`Table.Table enablePagination` is FORBIDDEN here.** It paginates the single server page it is
 * handed, so it would report "1 of 1" over 50 rows while the other 262 users stayed unreachable.
 *
 * **Zero- to one-based conversion happens here, in one marked place**, because `Pagination` counts
 * from 1 and the wire counts from 0. An off-by-one in this conversion silently skips or repeats a
 * page rather than failing, which is why it is isolated rather than spread across the callers.
 *
 * ## The desktop shape: centered, icon-only, and NO "Page n of m" line
 *
 * Corrected 2026-09-15 to the approved design. The prototype's `renderPager` emits **buttons only**
 * — First, Previous, the numbers, Next — inside a `.pager{justify-content:center}` within a
 * `.um-list-foot{justify-content:center}`. It shipped instead as a left-aligned "Page 1 of 7"
 * `Typography` beside a text **First** button with the numbers pushed to the right by
 * `marginLeft: auto`, which is a different control from the one that was approved.
 *
 * **That removal still stands at desktop.** The mobile shape re-introduces the line because it is
 * the only position indicator left once the numbers are gone — FR-014's "indicating the current
 * position" has to be satisfied by *something*, and at desktop it is satisfied instead by the
 * current numbered control's highlight and `aria-current="page"` (asserted by `ListPager.spec.tsx`).
 * Do not read the mobile line as a reversal of the 2026-09-15 correction at desktop.
 *
 * **First stays an Interstate `Button` rather than `Pagination`'s own `showFirstButton`, and that is
 * a localization constraint.** `showFirstButton` renders MUI's first-page control, whose accessible
 * name comes from MUI's default `getAriaLabel` — the hardcoded English "Go to first page", with no
 * prop on Interstate's `Pagination` to override it. Principle VI / FR-074 allow no UI text outside
 * `labels.json`, so First is rendered here as an icon-only `Button` whose `aria-label` is
 * `PAGER_FIRST`: the prototype's glyph **and** a localized name.
 */
export const ListPager = ({
  page,
  totalPages,
  onPageChange
}: ListPagerProps) => {
  // The one conversion. `page` is zero-based in; `Pagination` is one-based.
  const displayPage = page + 1;

  /*
   * No pager at all for a single page — there is nowhere to go, and rendering disabled controls
   * over one page of results is noise. `totalPages: 0` (a legitimate empty success) lands here too.
   */
  if (totalPages <= 1) {
    return null;
  }

  const isFirstPage = displayPage <= 1;
  const isLastPage = displayPage >= totalPages;

  return (
    <>
      <Box
        data-testid={"user-list-pager"}
        sx={{
          alignItems: "center",
          columnGap: "4px",
          ...desktopOnly("flex"),
          flexShrink: 0,
          /* Sized to fit one row — see `PAGER_CONTROL_WIDTH`; a wrap here is the defect, not the fallback. */
          flexWrap: "nowrap",
          justifyContent: "center",
          /* `.um-list-foot{padding:var(--ids-sem-space-3) 0 0}` — the rail's bands own their own gaps. */
          paddingTop: SPACE_BLOCK
        }}
      >
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-user-list-first-page",
            eventProperties: undefined
          }}
          aria-label={Locale.getText("PAGER_FIRST") as string}
          buttonStyle={"tertiary"}
          data-testid={"user-list-first-page"}
          disabled={isFirstPage}
          icon={<BackToStartSolidIcon title={""} />}
          /* The default medium icon button is 40px square, which the rail cannot spare. */
          size={"sm"}
          onClick={() => onPageChange(0)}
        />

        {/*
         * Previous / numbers / Next. `count` comes from the payload's `totalPages`, so no page is
         * ever offered that does not exist (T044).
         *
         * NO `showLastButton` and no trailing Last `Button` — FR-014 does not ask for one at
         * desktop, and a 312-user account's last page is not a place anyone needs to jump to. A
         * negative assertion covers this (T044) precisely because the sibling Login History pager
         * *does* have one, so "consistency" is the argument that would put it back. The mobile
         * shape below does render Last, because dropping the numbers frees the room for it and
         * leaves no other way to reach the end.
         */}
        <Pagination
          count={totalPages}
          data-testid={"user-list-pagination"}
          page={displayPage}
          sx={{
            /* MUI's own list wraps too, so the outer `nowrap` alone would not keep this to one row. */
            "& .MuiPagination-ul": { flexWrap: "nowrap" },
            "& .MuiPaginationItem-root": {
              height: PAGER_CONTROL_HEIGHT,
              margin: 0,
              minWidth: PAGER_CONTROL_WIDTH
            }
          }}
          onChange={nextDisplayPage => onPageChange(nextDisplayPage - 1)}
        />
      </Box>

      {/*
       * The mobile shape. Five controls at 48px plus the position line is ~310px in a 358px box, so
       * it fits a 390px viewport with slack and still fits a 375px iPhone SE (343px usable).
       *
       * `flexWrap: "wrap"` is deliberate and is the opposite of the desktop row's `nowrap`: here a
       * wrap is the graceful fallback for a long localized position string, not a defect.
       */}
      <Box
        data-testid={"user-list-pager-compact"}
        sx={{
          alignItems: "center",
          columnGap: "4px",
          ...mobileOnly("flex"),
          flexShrink: 0,
          flexWrap: "wrap",
          justifyContent: "center",
          paddingTop: SPACE_BLOCK,
          rowGap: "8px"
        }}
      >
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-user-list-first-page-compact",
            eventProperties: undefined
          }}
          aria-label={Locale.getText("PAGER_FIRST") as string}
          buttonStyle={"tertiary"}
          data-testid={"user-list-compact-first-page"}
          disabled={isFirstPage}
          icon={<BackToStartSolidIcon title={""} />}
          /* `lg` is the only stock size that clears 44px — see `TOUCH_TARGET_MIN`. */
          size={"lg"}
          onClick={() => onPageChange(0)}
        />

        <Button
          analyticsConfiguration={{
            eventName: "usermanager-user-list-previous-page-compact",
            eventProperties: undefined
          }}
          aria-label={Locale.getText("PREVIOUS_PAGE") as string}
          buttonStyle={"tertiary"}
          data-testid={"user-list-compact-previous-page"}
          disabled={isFirstPage}
          icon={<ChevronLeftIcon title={""} />}
          size={"lg"}
          onClick={() => onPageChange(page - 1)}
        />

        {/*
         * FR-014's "indicating the current position", for the shape that has no numbers to
         * highlight. `PAGER_POS` is already registered and already used by `LoginHistoryTab` —
         * preferred over the equally-registered `PAGE_OF` because its French reads "Page 3 sur 10"
         * rather than "Page 3 de 10", and because named parameters survive a translator reordering
         * them where `{0}`/`{1}` would silently swap the numbers.
         */}
        <Typography
          data-testid={"user-list-compact-position"}
          tag={"p"}
          variant={"body-sm"}
        >
          {String(
            Locale.getText("PAGER_POS", { page: displayPage, totalPages })
          )}
        </Typography>

        <Button
          analyticsConfiguration={{
            eventName: "usermanager-user-list-next-page-compact",
            eventProperties: undefined
          }}
          aria-label={Locale.getText("NEXT_PAGE") as string}
          buttonStyle={"tertiary"}
          data-testid={"user-list-compact-next-page"}
          disabled={isLastPage}
          icon={<ChevronRightIcon title={""} />}
          size={"lg"}
          onClick={() => onPageChange(page + 1)}
        />

        {/*
         * Last — rendered here and not at desktop. `BackToStartSolidIcon` mirrored rather than a
         * separate glyph, following `garage/DataTable.tsx`, because Interstate ships no
         * forward-to-end icon and `Pagination` never forwards `showLastButton` to MUI.
         */}
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-user-list-last-page-compact",
            eventProperties: undefined
          }}
          aria-label={Locale.getText("PAGER_LAST") as string}
          buttonStyle={"tertiary"}
          data-testid={"user-list-compact-last-page"}
          disabled={isLastPage}
          icon={
            /*
             * `style`, not `sx`. The icons re-exported from `core-components` are typed
             * `SVGProps<SVGSVGElement> & SvgProps` — plain SVG attributes, **not** MUI props — so an
             * `sx` here is silently dropped rather than rejected, and the arrow points the wrong way
             * with no error anywhere.
             */
            <BackToStartSolidIcon
              style={{ transform: "scaleX(-1)" }}
              title={""}
            />
          }
          size={"lg"}
          onClick={() => onPageChange(totalPages - 1)}
        />
      </Box>
    </>
  );
};
